import React__default, { forwardRef, useState, useRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, Platform, FlatList, TextInput, ScrollView, TouchableHighlight, Keyboard, ActivityIndicator, TouchableWithoutFeedback } from 'react-native';
import { A as AppAssets } from './index-3256db5b.esm.js';
import { u as useTheme, b as black } from './useTheme-90e8e2c2.esm.js';
import { A as AppBaseScreen } from './AppBaseScreen-6f697c5c.esm.js';
import { A as AppText, a as AppIcon } from './index-6b88191c.esm.js';
import { A as AppInputLabel } from './index-5ae71f5a.esm.js';
import { _ as _extends } from './extends-a2cb9f0c.esm.js';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import Qs from 'qs';
import { A as AppModal } from './index-9c3f5cad.esm.js';

const defaultStyles = {
  container: {
    flex: 1
  },
  textInputContainer: {
    flexDirection: "row",
    paddingLeft: 20,
    paddingRight: 20
    // backgroundColor: "red",
  },

  textInput: {
    backgroundColor: "#FFFFFF",
    height: 44,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 10,
    fontSize: 15,
    flex: 1,
    marginBottom: 5
  },
  loader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    height: 20
  },
  description: {},
  poweredContainer: {
    justifyContent: "flex-end",
    alignItems: "center",
    borderBottomRightRadius: 5,
    borderBottomLeftRadius: 5,
    borderColor: "#c8c7cc",
    borderTopWidth: 0.5
  },
  powered: {}
};
const AppLocationAutoComplete = /*#__PURE__*/forwardRef((props, ref) => {
  let _results = [];
  let _requests = [];
  const {
    currentTheme
  } = useTheme();
  const _styles = styleGenerator(currentTheme);
  const hasNavigator = () => {
    var _navigator;
    if ((_navigator = navigator) !== null && _navigator !== void 0 && _navigator.geolocation) {
      return true;
    } else {
      console.warn("If you are using React Native v0.60.0+ you must follow these instructions to enable currentLocation: https://git.io/Jf4AR");
      return false;
    }
  };
  const buildRowsFromResults = results => {
    let res = [];
    if (results.length === 0 || props.predefinedPlacesAlwaysVisible === true) {
      res = [...props.predefinedPlaces.filter(place => place === null || place === void 0 ? void 0 : place.description.length)];
      if (props.currentLocation === true && hasNavigator()) {
        res.unshift({
          description: props.currentLocationLabel,
          isCurrentLocation: true
        });
      }
    }
    res = res.map(place => ({
      ...place,
      isPredefinedPlace: true
    }));
    return [...res, ...results];
  };
  const getRequestUrl = requestUrl => {
    if (requestUrl) {
      if (requestUrl.useOnPlatform === "all") {
        return requestUrl.url;
      }
      if (requestUrl.useOnPlatform === "web") {
        return Platform.select({
          web: requestUrl.url,
          default: "https://maps.googleapis.com/maps/api"
        });
      }
    } else {
      return "https://maps.googleapis.com/maps/api";
    }
  };
  const [stateText, setStateText] = useState(props.value);
  const [dataSource, setDataSource] = useState(buildRowsFromResults([]));
  const [listViewDisplayed, setListViewDisplayed] = useState(props.listViewDisplayed === "auto" ? false : props.listViewDisplayed);
  const [url] = useState(getRequestUrl(props.requestUrl));
  const inputRef = useRef();
  useEffect(() => {
    // This will load the default value's search results after the view has
    // been rendered
    _handleChangeText(stateText);
    return () => {
      _abortRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Update dataSource if props.predefinedPlaces changed
    setDataSource(buildRowsFromResults([]));
  }, [props.predefinedPlaces]);
  useImperativeHandle(ref, () => ({
    setAddressText: address => {
      setStateText(address);
    },
    getAddressText: () => stateText,
    blur: () => inputRef.current.blur(),
    focus: () => inputRef.current.focus(),
    isFocused: () => inputRef.current.isFocused(),
    clear: () => inputRef.current.clear()
  }));
  const requestShouldUseWithCredentials = () => url === "https://maps.googleapis.com/maps/api";
  const _abortRequests = () => {
    _requests.forEach(i => i.abort());
    _requests = [];
  };
  const supportedPlatform = () => {
    if (Platform.OS === "web" && !props.requestUrl) {
      console.warn("This library cannot be used for the web unless you specify the requestUrl prop. See https://git.io/JflFv for more for details.");
      return false;
    } else {
      return true;
    }
  };
  const getCurrentLocation = () => {
    let options = {
      enableHighAccuracy: false,
      timeout: 20000,
      maximumAge: 1000
    };
    if (props.enableHighAccuracyLocation && Platform.OS === "android") {
      options = {
        enableHighAccuracy: true,
        timeout: 20000
      };
    }
    const getCurrentPosition = navigator.geolocation.getCurrentPosition || navigator.geolocation.default.getCurrentPosition;
    getCurrentPosition && getCurrentPosition(position => {
      if (props.nearbyPlacesAPI === "None") {
        let currentLocation = {
          description: props.currentLocationLabel,
          geometry: {
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }
          }
        };
        _disableRowLoaders();
        props.onPress(currentLocation, currentLocation);
      } else {
        _requestNearby(position.coords.latitude, position.coords.longitude);
      }
    }, error => {
      _disableRowLoaders();
      console.error(error.message);
    }, options);
  };
  const _onPress = rowData => {
    if (rowData.isPredefinedPlace !== true && props.fetchDetails === true) {
      if (rowData.isLoading === true) {
        // already requesting
        return;
      }
      Keyboard.dismiss();
      _abortRequests();

      // display loader
      _enableRowLoader(rowData);

      // fetch details
      const request = new XMLHttpRequest();
      _requests.push(request);
      request.timeout = props.timeout;
      request.ontimeout = props.onTimeout;
      request.onreadystatechange = () => {
        if (request.readyState !== 4) return;
        if (request.status === 200) {
          const responseJSON = JSON.parse(request.responseText);
          if (responseJSON.status === "OK") {
            // if (_isMounted === true) {
            const details = responseJSON.result;
            _disableRowLoaders();
            _onBlur();
            setStateText(_renderDescription(rowData));
            delete rowData.isLoading;
            props.onPress(rowData, details);
            // }
          } else {
            _disableRowLoaders();
            if (props.autoFillOnNotFound) {
              setStateText(_renderDescription(rowData));
              delete rowData.isLoading;
            }
            if (!props.onNotFound) {
              console.warn("google places autocomplete: " + responseJSON.status);
            } else {
              props.onNotFound(responseJSON);
            }
          }
        } else {
          _disableRowLoaders();
          if (!props.onFail) {
            console.warn("google places autocomplete: request could not be completed or has been aborted");
          } else {
            props.onFail("request could not be completed or has been aborted");
          }
        }
      };
      request.open("GET", "".concat(url, "/place/details/json?") + Qs.stringify({
        key: props.query.key,
        placeid: rowData.place_id,
        language: props.query.language,
        ...props.GooglePlacesDetailsQuery
      }));
      request.withCredentials = requestShouldUseWithCredentials();
      request.send();
    } else if (rowData.isCurrentLocation === true) {
      // display loader
      _enableRowLoader(rowData);
      setStateText(_renderDescription(rowData));
      delete rowData.isLoading;
      getCurrentLocation();
    } else {
      setStateText(_renderDescription(rowData));
      _onBlur();
      delete rowData.isLoading;
      let predefinedPlace = _getPredefinedPlace(rowData);

      // sending predefinedPlace as details for predefined places
      props.onPress(predefinedPlace, predefinedPlace);
    }
  };
  const _enableRowLoader = rowData => {
    let rows = buildRowsFromResults(_results);
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].place_id === rowData.place_id || rows[i].isCurrentLocation === true && rowData.isCurrentLocation === true) {
        rows[i].isLoading = true;
        setDataSource(rows);
        break;
      }
    }
  };
  const _disableRowLoaders = () => {
    // if (_isMounted === true) {
    for (let i = 0; i < _results.length; i++) {
      if (_results[i].isLoading === true) {
        _results[i].isLoading = false;
      }
    }
    setDataSource(buildRowsFromResults(_results));
    // }
  };

  const _getPredefinedPlace = rowData => {
    if (rowData.isPredefinedPlace !== true) {
      return rowData;
    }
    for (let i = 0; i < props.predefinedPlaces.length; i++) {
      if (props.predefinedPlaces[i].description === rowData.description) {
        return props.predefinedPlaces[i];
      }
    }
    return rowData;
  };
  const _filterResultsByTypes = (unfilteredResults, types) => {
    if (types.length === 0) return unfilteredResults;
    const results = [];
    for (let i = 0; i < unfilteredResults.length; i++) {
      let found = false;
      for (let j = 0; j < types.length; j++) {
        if (unfilteredResults[i].types.indexOf(types[j]) !== -1) {
          found = true;
          break;
        }
      }
      if (found === true) {
        results.push(unfilteredResults[i]);
      }
    }
    return results;
  };
  const _requestNearby = (latitude, longitude) => {
    _abortRequests();
    if (latitude !== undefined && longitude !== undefined && latitude !== null && longitude !== null) {
      const request = new XMLHttpRequest();
      _requests.push(request);
      request.timeout = props.timeout;
      request.ontimeout = props.onTimeout;
      request.onreadystatechange = () => {
        if (request.readyState !== 4) {
          return;
        }
        if (request.status === 200) {
          const responseJSON = JSON.parse(request.responseText);
          _disableRowLoaders();
          if (typeof responseJSON.results !== "undefined") {
            // if (_isMounted === true) {
            var results = [];
            if (props.nearbyPlacesAPI === "GoogleReverseGeocoding") {
              results = _filterResultsByTypes(responseJSON.results, props.filterReverseGeocodingByTypes);
            } else {
              results = responseJSON.results;
            }
            setDataSource(buildRowsFromResults(results));
            // }
          }

          if (typeof responseJSON.error_message !== "undefined") {
            if (!props.onFail) console.warn("google places autocomplete: " + responseJSON.error_message);else {
              props.onFail(responseJSON.error_message);
            }
          }
        }
      };
      let requestUrl = "";
      if (props.nearbyPlacesAPI === "GoogleReverseGeocoding") {
        // your key must be allowed to use Google Maps Geocoding API
        requestUrl = "".concat(url, "/geocode/json?") + Qs.stringify({
          latlng: latitude + "," + longitude,
          key: props.query.key,
          ...props.GoogleReverseGeocodingQuery
        });
      } else {
        requestUrl = "".concat(url, "/place/nearbysearch/json?") + Qs.stringify({
          location: latitude + "," + longitude,
          key: props.query.key,
          ...props.GooglePlacesSearchQuery
        });
      }
      request.open("GET", requestUrl);
      request.withCredentials = requestShouldUseWithCredentials();
      request.send();
    } else {
      _results = [];
      setDataSource(buildRowsFromResults([]));
    }
  };
  const _request = text => {
    _abortRequests();
    if (supportedPlatform() && text && text.length >= props.minLength) {
      const request = new XMLHttpRequest();
      _requests.push(request);
      request.timeout = props.timeout;
      request.ontimeout = props.onTimeout;
      request.onreadystatechange = () => {
        if (request.readyState !== 4) {
          return;
        }
        if (request.status === 200) {
          const responseJSON = JSON.parse(request.responseText);
          if (typeof responseJSON.predictions !== "undefined") {
            // if (_isMounted === true) {
            const results = props.nearbyPlacesAPI === "GoogleReverseGeocoding" ? _filterResultsByTypes(responseJSON.predictions, props.filterReverseGeocodingByTypes) : responseJSON.predictions;
            _results = results;
            setDataSource(buildRowsFromResults(results));
            // }
          }

          if (typeof responseJSON.error_message !== "undefined") {
            if (!props.onFail) console.warn("google places autocomplete: " + responseJSON.error_message);else {
              props.onFail(responseJSON.error_message);
            }
          }
        }
      };
      if (props.preProcess) {
        setStateText(props.preProcess(text));
      }
      request.open("GET", "".concat(url, "/place/autocomplete/json?&input=") + encodeURIComponent(text) + "&" + Qs.stringify(props.query));
      request.withCredentials = requestShouldUseWithCredentials();
      request.send();
    } else {
      _results = [];
      setDataSource(buildRowsFromResults([]));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceData = useMemo(() => debounce(_request, props.debounce), [props.query]);
  const _onChangeText = text => {
    setStateText(text);
    debounceData(text);
  };
  const _handleChangeText = text => {
    var _props$textInputProps;
    _onChangeText(text);
    const onChangeText = props === null || props === void 0 ? void 0 : (_props$textInputProps = props.textInputProps) === null || _props$textInputProps === void 0 ? void 0 : _props$textInputProps.onChangeText;
    if (onChangeText) {
      onChangeText(text);
    }
  };
  const _getRowLoader = () => {
    return /*#__PURE__*/React__default.createElement(ActivityIndicator, {
      animating: true,
      size: "small"
    });
  };
  const _renderRowData = (rowData, index) => {
    if (props.renderRow) {
      return props.renderRow(rowData, index);
    }
    return /*#__PURE__*/React__default.createElement(AppText, {
      style: [props.suppressDefaultStyles ? {} : defaultStyles.description, props.styles.description, rowData.isPredefinedPlace ? props.styles.predefinedPlacesDescription : {}],
      numberOfLines: props.numberOfLines
    }, _renderDescription(rowData));
  };
  const _renderDescription = rowData => {
    if (props.renderDescription) {
      return props.renderDescription(rowData);
    }
    return rowData.description || rowData.formatted_address || rowData.name;
  };
  const _renderLoader = rowData => {
    if (rowData.isLoading === true) {
      return /*#__PURE__*/React__default.createElement(View, {
        style: [props.suppressDefaultStyles ? {} : defaultStyles.loader, props.styles.loader]
      }, _getRowLoader());
    }
    return null;
  };
  const _renderRow = function () {
    let rowData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let index = arguments.length > 1 ? arguments[1] : undefined;
    return /*#__PURE__*/React__default.createElement(ScrollView, {
      contentContainerStyle: props.isRowScrollable ? {
        minWidth: "100%"
      } : {
        width: "100%"
      },
      scrollEnabled: props.isRowScrollable,
      keyboardShouldPersistTaps: props.keyboardShouldPersistTaps,
      horizontal: true,
      showsHorizontalScrollIndicator: false,
      showsVerticalScrollIndicator: false
    }, /*#__PURE__*/React__default.createElement(TouchableHighlight, {
      style: props.isRowScrollable ? {
        minWidth: "100%"
      } : {
        width: "100%"
      },
      onPress: () => _onPress(rowData),
      underlayColor: props.listUnderlayColor || "#c8c7cc"
    }, /*#__PURE__*/React__default.createElement(View, {
      style: [props.suppressDefaultStyles ? {} : _styles.row, props.styles.row, rowData.isPredefinedPlace ? props.styles.specialItemRow : {}]
    }, _renderLoader(rowData), _renderRowData(rowData, index))));
  };
  const _renderSeparator = (sectionID, rowID) => {
    if (rowID === dataSource.length - 1) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(View, {
      key: "".concat(sectionID, "-").concat(rowID),
      style: [props.suppressDefaultStyles ? {} : _styles.separator, props.styles.separator]
    });
  };
  const isNewFocusInAutocompleteResultList = _ref => {
    let {
      relatedTarget,
      currentTarget
    } = _ref;
    if (!relatedTarget) return false;
    var node = relatedTarget.parentNode;
    while (node) {
      if (node.id === "result-list-id") return true;
      node = node.parentNode;
    }
    return false;
  };
  const _onBlur = e => {
    var _inputRef$current;
    if (e && isNewFocusInAutocompleteResultList(e)) return;
    setListViewDisplayed(false);
    inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur();
  };
  const _onFocus = () => setListViewDisplayed(true);
  const _renderPoweredLogo = () => {
    if (!_shouldShowPoweredLogo()) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(View, {
      style: [props.suppressDefaultStyles ? {} : _styles.row, defaultStyles.poweredContainer, props.styles.poweredContainer]
    });
  };
  const _shouldShowPoweredLogo = () => {
    if (!props.enablePoweredByContainer || dataSource.length === 0) {
      return false;
    }
    for (let i = 0; i < dataSource.length; i++) {
      let row = dataSource[i];
      if (!row.hasOwnProperty("isCurrentLocation") && !row.hasOwnProperty("isPredefinedPlace")) {
        return true;
      }
    }
    return false;
  };
  const _renderLeftButton = () => {
    if (props.renderLeftButton) {
      return props.renderLeftButton();
    }
  };
  const _renderRightButton = () => {
    if (props.renderRightButton) {
      return props.renderRightButton();
    }
  };
  const _getFlatList = () => {
    const keyGenerator = () => Math.random().toString(36).substr(2, 10);
    if (supportedPlatform() && (stateText !== "" || props.predefinedPlaces.length > 0 || props.currentLocation === true) && listViewDisplayed === true) {
      return /*#__PURE__*/React__default.createElement(FlatList, _extends({
        nativeID: "result-list-id",
        scrollEnabled: !props.disableScroll,
        style: [props.suppressDefaultStyles ? {} : _styles.listView, props.styles.listView],
        data: dataSource,
        keyExtractor: keyGenerator,
        extraData: [dataSource, props],
        ItemSeparatorComponent: _renderSeparator,
        renderItem: _ref2 => {
          let {
            item,
            index
          } = _ref2;
          return _renderRow(item, index);
        },
        ListEmptyComponent: stateText.length > props.minLength && props.listEmptyComponent,
        ListHeaderComponent: props.renderHeaderComponent && props.renderHeaderComponent(stateText),
        ListFooterComponent: _renderPoweredLogo
      }, props));
    }
    return null;
  };
  let {
    onFocus,
    onBlur,
    onChangeText,
    // destructuring here stops this being set after onChangeText={_handleChangeText}
    clearButtonMode,
    InputComp,
    ...userProps
  } = props.textInputProps;
  const TextInputComp = InputComp || TextInput;
  return /*#__PURE__*/React__default.createElement(View, {
    style: [props.suppressDefaultStyles ? {} : defaultStyles.container, props.styles.container],
    pointerEvents: "box-none"
  }, !props.textInputHide && /*#__PURE__*/React__default.createElement(View, {
    style: [props.suppressDefaultStyles ? {} : defaultStyles.textInputContainer, props.styles.textInputContainer]
  }, _renderLeftButton(), /*#__PURE__*/React__default.createElement(TextInputComp, _extends({
    forwardRef: inputRef,
    style: [_styles.textInputStyle],
    value: stateText,
    placeholder: props.placeholder,
    onFocus: onFocus ? () => {
      _onFocus();
      onFocus();
    } : _onFocus,
    onBlur: onBlur ? e => {
      _onBlur(e);
      onBlur();
    } : _onBlur,
    clearButtonMode: clearButtonMode || "while-editing",
    onChangeText: _handleChangeText
  }, userProps)), _renderRightButton()), _getFlatList(), props.children);
});
const styleGenerator = props => {
  var _props$theme, _props$theme2, _props$theme3, _props$theme4, _props$theme5;
  return StyleSheet.create({
    textInputStyle: {
      paddingLeft: 20,
      paddingRight: 20,
      flex: 1,
      height: 50,
      fontSize: props === null || props === void 0 ? void 0 : (_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : _props$theme.medium_font_size,
      color: props === null || props === void 0 ? void 0 : (_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : _props$theme2.font_color,
      fontFamily: "normalFont",
      backgroundColor: props === null || props === void 0 ? void 0 : (_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : _props$theme3.inputBackgroundColor,
      borderRadius: 10
    },
    listView: {
      padding: 20
    },
    row: {
      backgroundColor: props === null || props === void 0 ? void 0 : (_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : _props$theme4.pickerMenuColor,
      padding: 13,
      minHeight: 44,
      flexDirection: "row"
    },
    separator: {
      height: 0.4,
      backgroundColor: props === null || props === void 0 ? void 0 : (_props$theme5 = props.theme) === null || _props$theme5 === void 0 ? void 0 : _props$theme5.grey
    }
  });
};
AppLocationAutoComplete.propTypes = {
  autoFillOnNotFound: PropTypes.bool,
  currentLocation: PropTypes.bool,
  currentLocationLabel: PropTypes.string,
  debounce: PropTypes.number,
  disableScroll: PropTypes.bool,
  enableHighAccuracyLocation: PropTypes.bool,
  enablePoweredByContainer: PropTypes.bool,
  fetchDetails: PropTypes.bool,
  filterReverseGeocodingByTypes: PropTypes.array,
  GooglePlacesDetailsQuery: PropTypes.object,
  GooglePlacesSearchQuery: PropTypes.object,
  GoogleReverseGeocodingQuery: PropTypes.object,
  isRowScrollable: PropTypes.bool,
  keyboardShouldPersistTaps: PropTypes.oneOf(["never", "always", "handled"]),
  listEmptyComponent: PropTypes.func,
  listUnderlayColor: PropTypes.string,
  listViewDisplayed: PropTypes.oneOf(["auto", PropTypes.bool]),
  minLength: PropTypes.number,
  nearbyPlacesAPI: PropTypes.string,
  numberOfLines: PropTypes.number,
  onFail: PropTypes.func,
  onNotFound: PropTypes.func,
  onPress: PropTypes.func,
  onTimeout: PropTypes.func,
  placeholder: PropTypes.string,
  predefinedPlaces: PropTypes.array,
  predefinedPlacesAlwaysVisible: PropTypes.bool,
  preProcess: PropTypes.func,
  query: PropTypes.object,
  renderDescription: PropTypes.func,
  renderHeaderComponent: PropTypes.func,
  renderLeftButton: PropTypes.func,
  renderRightButton: PropTypes.func,
  renderRow: PropTypes.func,
  requestUrl: PropTypes.shape({
    url: PropTypes.string,
    useOnPlatform: PropTypes.oneOf(["web", "all"])
  }),
  styles: PropTypes.object,
  suppressDefaultStyles: PropTypes.bool,
  textInputHide: PropTypes.bool,
  textInputProps: PropTypes.object,
  timeout: PropTypes.number
};
AppLocationAutoComplete.defaultProps = {
  autoFillOnNotFound: false,
  currentLocation: false,
  currentLocationLabel: "Current location",
  debounce: 0,
  disableScroll: false,
  enableHighAccuracyLocation: true,
  enablePoweredByContainer: true,
  fetchDetails: false,
  filterReverseGeocodingByTypes: [],
  GooglePlacesDetailsQuery: {},
  GooglePlacesSearchQuery: {
    rankby: "distance",
    type: "cities"
  },
  GoogleReverseGeocodingQuery: {},
  isRowScrollable: true,
  keyboardShouldPersistTaps: "always",
  listUnderlayColor: "#c8c7cc",
  listViewDisplayed: "auto",
  minLength: 0,
  nearbyPlacesAPI: "GooglePlacesSearch",
  numberOfLines: 1,
  onFail: e => console.warn("google places has failed", JSON.stringify(e)),
  onNotFound: () => console.warn("google places not found"),
  onPress: e => {},
  onTimeout: () => console.warn("google places autocomplete: request timeout"),
  placeholder: "",
  predefinedPlaces: [],
  predefinedPlacesAlwaysVisible: false,
  query: {
    key: "missing api key",
    language: "en",
    types: "geocode"
  },
  styles: {},
  suppressDefaultStyles: false,
  textInputHide: false,
  textInputProps: {
    onChangeText: t => {}
  },
  timeout: 20000
};
AppLocationAutoComplete.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppLocationAutoComplete",
  "props": {
    "autoFillOnNotFound": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "currentLocation": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "currentLocationLabel": {
      "defaultValue": {
        "value": "\"Current location\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "string"
      },
      "required": false
    },
    "debounce": {
      "defaultValue": {
        "value": "0",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "number"
      },
      "required": false
    },
    "disableScroll": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "enableHighAccuracyLocation": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "enablePoweredByContainer": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "fetchDetails": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "filterReverseGeocodingByTypes": {
      "defaultValue": {
        "value": "[]",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "array"
      },
      "required": false
    },
    "GooglePlacesDetailsQuery": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "GooglePlacesSearchQuery": {
      "defaultValue": {
        "value": "{\n  rankby: \"distance\",\n  type: \"cities\",\n}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "GoogleReverseGeocodingQuery": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "isRowScrollable": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "keyboardShouldPersistTaps": {
      "defaultValue": {
        "value": "\"always\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "enum",
        "value": [{
          "value": "\"never\"",
          "computed": false
        }, {
          "value": "\"always\"",
          "computed": false
        }, {
          "value": "\"handled\"",
          "computed": false
        }]
      },
      "required": false
    },
    "listUnderlayColor": {
      "defaultValue": {
        "value": "\"#c8c7cc\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "string"
      },
      "required": false
    },
    "listViewDisplayed": {
      "defaultValue": {
        "value": "\"auto\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "enum",
        "value": [{
          "value": "\"auto\"",
          "computed": false
        }, {
          "value": "PropTypes.bool",
          "computed": true
        }]
      },
      "required": false
    },
    "minLength": {
      "defaultValue": {
        "value": "0",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "number"
      },
      "required": false
    },
    "nearbyPlacesAPI": {
      "defaultValue": {
        "value": "\"GooglePlacesSearch\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "string"
      },
      "required": false
    },
    "numberOfLines": {
      "defaultValue": {
        "value": "1",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "number"
      },
      "required": false
    },
    "onFail": {
      "defaultValue": {
        "value": "(e) => console.warn(\"google places has failed\", JSON.stringify(e))",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "onNotFound": {
      "defaultValue": {
        "value": "() => console.warn(\"google places not found\")",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "onPress": {
      "defaultValue": {
        "value": "(e) => {}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "onTimeout": {
      "defaultValue": {
        "value": "() => console.warn(\"google places autocomplete: request timeout\")",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "placeholder": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "string"
      },
      "required": false
    },
    "predefinedPlaces": {
      "defaultValue": {
        "value": "[]",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "array"
      },
      "required": false
    },
    "predefinedPlacesAlwaysVisible": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "query": {
      "defaultValue": {
        "value": "{\n  key: \"missing api key\",\n  language: \"en\",\n  types: \"geocode\",\n}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "styles": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "suppressDefaultStyles": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "textInputHide": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "bool"
      },
      "required": false
    },
    "textInputProps": {
      "defaultValue": {
        "value": "{\n  onChangeText: (t) => {},\n}",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "object"
      },
      "required": false
    },
    "timeout": {
      "defaultValue": {
        "value": "20000",
        "computed": false
      },
      "description": "",
      "type": {
        "name": "number"
      },
      "required": false
    },
    "listEmptyComponent": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "preProcess": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "renderDescription": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "renderHeaderComponent": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "renderLeftButton": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "renderRightButton": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "renderRow": {
      "description": "",
      "type": {
        "name": "func"
      },
      "required": false
    },
    "requestUrl": {
      "description": "",
      "type": {
        "name": "shape",
        "value": {
          "url": {
            "name": "string",
            "required": false
          },
          "useOnPlatform": {
            "name": "enum",
            "value": [{
              "value": "\"web\"",
              "computed": false
            }, {
              "value": "\"all\"",
              "computed": false
            }],
            "required": false
          }
        }
      },
      "required": false
    }
  }
};

function AppPickerItem(_ref) {
  let {
    item,
    optionLabel,
    onPress,
    itemContainerStyle,
    itemLabelStyle,
    itemFontSize = 18
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const _pickerStyle = pickerStyle(currentTheme, itemFontSize);
  return /*#__PURE__*/React__default.createElement(TouchableWithoutFeedback, {
    onPress: onPress
  }, /*#__PURE__*/React__default.createElement(View, {
    style: [_pickerStyle.itemContainer, itemContainerStyle, {
      cursor: Platform.OS === "web" && "pointer"
    }]
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: [_pickerStyle.item, itemLabelStyle]
  }, optionLabel ? item[optionLabel] : item)));
}
const pickerStyle = (props, itemFontSize) => {
  return StyleSheet.create({
    itemContainer: {
      padding: 10
    },
    item: {
      fontSize: itemFontSize
    }
  });
};
AppPickerItem.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppPickerItem",
  "props": {
    "itemFontSize": {
      "defaultValue": {
        "value": "18",
        "computed": false
      },
      "required": false
    }
  }
};

function AppPicker(_ref) {
  var _currentTheme$theme, _currentTheme$theme2;
  let {
    onSelectItem,
    selectedItem,
    width,
    icon,
    defaultIcon,
    placeholder,
    options,
    label,
    isMandatory,
    optionLabel = "label",
    optionValue,
    optionKey,
    showTextInputLabel,
    googleLocationApiKey,
    PickerItemComponent = AppPickerItem,
    numberOfColumns,
    itemContainerStyle,
    itemLabelStyle,
    itemFontSize,
    pickerType,
    modalScreenStyle,
    textStyle,
    wrapperInputStyle,
    title = "Choose",
    transparent = true,
    modalBackdrop,
    inputLabelStyle = {}
  } = _ref;
  const exposedValueProperty = optionValue || optionLabel;
  const [selectedVal, setSelectedVal] = useState(selectedItem ? selectedItem : null);
  const [modalOpen, setModalOpen] = useState(false);
  const {
    currentTheme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const styles = generateStyle(currentTheme, selectedVal);
  const onPress = function (item) {
    let shouldClose = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    setModalOpen(!shouldClose);
    onSelectItem && onSelectItem(exposedValueProperty && pickerType !== "emoji" ? item[exposedValueProperty] : item);
    setSelectedVal(exposedValueProperty ? item[exposedValueProperty] : item);
  };
  useEffect(() => {
    setSelectedVal(selectedItem);
  }, [selectedItem]);
  return /*#__PURE__*/React__default.createElement(View, {
    style: wrapperInputStyle
  }, /*#__PURE__*/React__default.createElement(TouchableWithoutFeedback, {
    onPress: () => setModalOpen(!modalOpen)
  }, /*#__PURE__*/React__default.createElement(View, {
    style: styles.wrapperContainer
  }, showTextInputLabel ? /*#__PURE__*/React__default.createElement(AppInputLabel, {
    style: {
      marginLeft: 10,
      ...inputLabelStyle
    },
    isMandatory: isMandatory,
    label: label
  }) : null, /*#__PURE__*/React__default.createElement(View, {
    style: [styles.container, {
      width: width
    }]
  }, icon && /*#__PURE__*/React__default.createElement(AppIcon, {
    color: black,
    name: icon,
    background: "transparent"
  }), /*#__PURE__*/React__default.createElement(AppText, {
    preventTranslation: pickerType === "emoji",
    wrapperStyle: {
      flex: 1
    },
    style: [styles.baseText, textStyle]
  }, selectedVal ? selectedVal : t(placeholder)), /*#__PURE__*/React__default.createElement(AppIcon, {
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.font_color,
    icon: defaultIcon || AppAssets.icons.arrow_down,
    background: "transparent",
    size: 20,
    style: {
      position: "relative",
      right: 15,
      flex: 0.2
    }
  })))), /*#__PURE__*/React__default.createElement(AppModal, {
    isCloseButtonVisible: false,
    transparent: transparent,
    modalBackdropStatic: false,
    modalStyle: Platform.OS === "web" ? {
      maxWidth: 600,
      maxHeight: "80%",
      margin: "auto",
      borderRadius: 10
    } : {},
    closeModal: () => {
      setModalOpen(false);
    },
    isModalVisible: modalOpen,
    animationType: "none",
    modalBackdrop: modalBackdrop
  }, /*#__PURE__*/React__default.createElement(AppBaseScreen, {
    style: [styles.baseModalScreenStyle, modalScreenStyle],
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.backgroundColor,
    isScrollable: false,
    contentContainerStyle: {
      maxHeight: 400,
      padding: 20
    }
  }, pickerType === "city" && /*#__PURE__*/React__default.createElement(AppLocationAutoComplete, {
    value: selectedItem,
    onPress: (v, b) => onPress(v, false),
    query: {
      key: googleLocationApiKey,
      language: "en"
    },
    placeholder: t("Search")
  }), (pickerType === "emoji" || !pickerType) && /*#__PURE__*/React__default.createElement(FlatList, {
    contentContainerStyle: {
      padding: 5
    },
    numColumns: numberOfColumns,
    data: options,
    keyExtractor: item => optionKey ? item[optionKey].toString() : item.toString(),
    renderItem: _ref2 => {
      let {
        item
      } = _ref2;
      return /*#__PURE__*/React__default.createElement(PickerItemComponent, {
        optionLabel: pickerType !== "emoji" && optionLabel,
        itemContainerStyle: itemContainerStyle,
        itemLabelStyle: itemLabelStyle,
        itemFontSize: itemFontSize,
        item: item,
        onPress: () => {
          onPress(item);
        }
      });
    }
  }))));
}
const generateStyle = (props, selectedVal) => {
  var _props$theme, _props$theme2, _props$theme3, _props$theme4, _props$theme5, _props$theme6;
  return StyleSheet.create({
    baseModalScreenStyle: {
      justifyContent: "center",
      alignItems: "center",
      padding: 20
    },
    baseText: {
      flex: 1,
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 15,
      color: selectedVal ? props === null || props === void 0 ? void 0 : (_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : _props$theme.darkGrey : props === null || props === void 0 ? void 0 : (_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : _props$theme2.inputPlaceholderColor,
      fontSize: props === null || props === void 0 ? void 0 : (_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : _props$theme3.small_font_size
    },
    wrapperContainer: {
      flexDirection: "column",
      backgroundColor: "transparent"
      // shadowOpacity: 0.3,
      // shadowRadius: 4.84,
      // shadowColor: props?.theme?.shadowColor,
      // shadowOffset: {
      //   width: 3,
      //   height: 3,
      // },

      // paddingLeft: 10,
      // paddingRight: 10,
    },

    closeButton: {
      marginBottom: 10,
      alignSelf: "center",
      width: 240
    },
    container: {
      flexDirection: "row",
      borderRadius: 10,
      borderColor: props === null || props === void 0 ? void 0 : (_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : _props$theme4.border_color,
      borderWidth: 1,
      alignItems: "center",
      margin: 5,
      backgroundColor: props === null || props === void 0 ? void 0 : (_props$theme5 = props.theme) === null || _props$theme5 === void 0 ? void 0 : _props$theme5.inputBackgroundColor,
      height: props === null || props === void 0 ? void 0 : (_props$theme6 = props.theme) === null || _props$theme6 === void 0 ? void 0 : _props$theme6.fieldHeight
    },
    listIconsContainer: {
      flex: 1,
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center"
    }
  });
};
AppPicker.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppPicker",
  "props": {
    "optionLabel": {
      "defaultValue": {
        "value": "\"label\"",
        "computed": false
      },
      "required": false
    },
    "PickerItemComponent": {
      "defaultValue": {
        "value": "AppPickerItem",
        "computed": true
      },
      "required": false
    },
    "title": {
      "defaultValue": {
        "value": "\"Choose\"",
        "computed": false
      },
      "required": false
    },
    "transparent": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "required": false
    },
    "inputLabelStyle": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "required": false
    }
  }
};

// Code used to leverage third party libraries
// {props.isOptionWithIcon ? (
//   <View style={styles.listIconsContainer}>
//     {props.options.map((item) => {
//       return (
//         <AppIcon

//           background={item.background}
//           name={item.icon}
//           label={item.label}
//           onPress={() => {
//             onPress(item);
//           }}
//         />
//       );
//     })}
//   </View>
// ) : (
//   <FlatList
//     data={props.options}
//     keyExtractor={(item) => item.label}
//     renderItem={({ item }) => {
//       return (
//         <AppPickerItem
//           item={item}
//           onPress={() => {
//             onPress(item);
//           }}
//         />
//       );
//     }}
//   />
// )}

export { AppPicker as A, AppPickerItem as a };
